import { isRecord } from "utils/isRecord";

export const PROPOSE_TENANT_DOCUMENTS_TOOL_NAME = "propose_tenant_documents_for_ingestion";

/** Current tool-result kind for tenant-document ingestion proposals. */
export const TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND = "tenant_document_ingestion";

/**
 * Legacy kind emitted before the shared awaiting_user_confirmation shape.
 * Kept so proposal cards still rehydrate from older conversation history.
 */
const LEGACY_TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND = "tenant_document_ingestion_proposal";

export type ScoutTenantDocumentProposalDocument = {
  id: string;
  title: string;
  fileExtension: string;
  documentType: string;
  createdAt: string;
  sizeMegabytes: number | null;
};

export type ScoutTenantDocumentProposal = {
  proposalId: string;
  documents: ScoutTenantDocumentProposalDocument[];
  missingDocumentIds: string[];
};

function readString(value: Record<string, unknown>, key: string): string | undefined {
  const candidate = value[key];
  return typeof candidate === "string" ? candidate : undefined;
}

function readNumber(value: Record<string, unknown>, key: string): number | null {
  const candidate = value[key];
  return typeof candidate === "number" && Number.isFinite(candidate) ? candidate : null;
}

/**
 * Normalize a shared-shape `items[]` entry or a legacy `documents[]` entry
 * into the card's document view model. Shared shape uses `label`; legacy
 * used `title`.
 */
function readDocumentItem(item: unknown): ScoutTenantDocumentProposalDocument | null {
  if (!isRecord(item)) return null;
  const id = readString(item, "id");
  const title = readString(item, "label") ?? readString(item, "title");
  if (!id || !title) return null;
  return {
    id,
    title,
    fileExtension: readString(item, "fileExtension") ?? "",
    documentType: readString(item, "documentType") ?? "",
    createdAt: readString(item, "createdAt") ?? "",
    sizeMegabytes: readNumber(item, "sizeMegabytes"),
  };
}

function readDocuments(value: unknown): ScoutTenantDocumentProposalDocument[] {
  if (!Array.isArray(value)) return [];
  const documents: ScoutTenantDocumentProposalDocument[] = [];
  for (const item of value) {
    const document = readDocumentItem(item);
    if (document) documents.push(document);
  }
  return documents;
}

function readMissing(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}

function isTenantDocumentIngestionProposalKind(kind: unknown): boolean {
  return (
    kind === TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND ||
    kind === LEGACY_TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND
  );
}

export function parseScoutTenantDocumentProposal(
  output: unknown
): ScoutTenantDocumentProposal | null {
  if (!isRecord(output)) return null;
  if (!isTenantDocumentIngestionProposalKind(output.kind)) return null;
  const proposalId = readString(output, "proposalId");
  if (!proposalId) return null;
  // Prefer shared-shape `items`; fall back to legacy `documents`.
  const documents = readDocuments(Array.isArray(output.items) ? output.items : output.documents);
  const missingDocumentIds = readMissing(output.missingDocumentIds);
  return { proposalId, documents, missingDocumentIds };
}
